"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createCenterConstraint = createCenterConstraint;
exports.createResolutionConstraint = createResolutionConstraint;
exports.createRotationConstraint = createRotationConstraint;
exports["default"] = void 0;
exports.isNoopAnimation = isNoopAnimation;

var _Object = _interopRequireDefault(require("./Object.js"));

var _GeometryType = _interopRequireDefault(require("./geom/GeometryType.js"));

var _Units = _interopRequireDefault(require("./proj/Units.js"));

var _ViewHint = _interopRequireDefault(require("./ViewHint.js"));

var _ViewProperty = _interopRequireDefault(require("./ViewProperty.js"));

var _common = require("./tilegrid/common.js");

var _proj = require("./proj.js");

var _functions = require("./functions.js");

var _coordinate = require("./coordinate.js");

var _asserts = require("./asserts.js");

var _obj = require("./obj.js");

var _centerconstraint = require("./centerconstraint.js");

var _math = require("./math.js");

var _resolutionconstraint = require("./resolutionconstraint.js");

var _rotationconstraint = require("./rotationconstraint.js");

var _easing = require("./easing.js");

var _extent = require("./extent.js");

var _array = require("./array.js");

var _Polygon = require("./geom/Polygon.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

var __extends = void 0 && (void 0).__extends || function () {
  var _extendStatics = function extendStatics(d, b) {
    _extendStatics = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p];
      }
    };

    return _extendStatics(d, b);
  };

  return function (d, b) {
    if (typeof b !== "function" && b !== null) throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");

    _extendStatics(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();
/**
 * @module ol/View
 */


/**
 * An animation configuration
 *
 * @typedef {Object} Animation
 * @property {import("./coordinate.js").Coordinate} [sourceCenter] Source center.
 * @property {import("./coordinate.js").Coordinate} [targetCenter] Target center.
 * @property {number} [sourceResolution] Source resolution.
 * @property {number} [targetResolution] Target resolution.
 * @property {number} [sourceRotation] Source rotation.
 * @property {number} [targetRotation] Target rotation.
 * @property {import("./coordinate.js").Coordinate} [anchor] Anchor.
 * @property {number} start Start.
 * @property {number} duration Duration.
 * @property {boolean} complete Complete.
 * @property {function(number):number} easing Easing.
 * @property {function(boolean):void} callback Callback.
 */

/**
 * @typedef {Object} Constraints
 * @property {import("./centerconstraint.js").Type} center Center.
 * @property {import("./resolutionconstraint.js").Type} resolution Resolution.
 * @property {import("./rotationconstraint.js").Type} rotation Rotation.
 */

/**
 * @typedef {Object} FitOptions
 * @property {import("./size.js").Size} [size] The size in pixels of the box to fit
 * the extent into. Default is the current size of the first map in the DOM that
 * uses this view, or `[100, 100]` if no such map is found.
 * @property {!Array<number>} [padding=[0, 0, 0, 0]] Padding (in pixels) to be
 * cleared inside the view. Values in the array are top, right, bottom and left
 * padding.
 * @property {boolean} [nearest=false] If the view `constrainResolution` option is `true`,
 * get the nearest extent instead of the closest that actually fits the view.
 * @property {number} [minResolution=0] Minimum resolution that we zoom to.
 * @property {number} [maxZoom] Maximum zoom level that we zoom to. If
 * `minResolution` is given, this property is ignored.
 * @property {number} [duration] The duration of the animation in milliseconds.
 * By default, there is no animation to the target extent.
 * @property {function(number):number} [easing] The easing function used during
 * the animation (defaults to {@link module:ol/easing.inAndOut}).
 * The function will be called for each frame with a number representing a
 * fraction of the animation's duration.  The function should return a number
 * between 0 and 1 representing the progress toward the destination state.
 * @property {function(boolean):void} [callback] Function called when the view is in
 * its final position. The callback will be called with `true` if the animation
 * series completed on its own or `false` if it was cancelled.
 */

/**
 * @typedef {Object} ViewOptions
 * @property {import("./coordinate.js").Coordinate} [center] The initial center for
 * the view. If a user projection is not set, the coordinate system for the center is
 * specified with the `projection` option. Layer sources will not be fetched if this
 * is not set, but the center can be set later with {@link #setCenter}.
 * @property {boolean|number} [constrainRotation=true] Rotation constraint.
 * `false` means no constraint. `true` means no constraint, but snap to zero
 * near zero. A number constrains the rotation to that number of values. For
 * example, `4` will constrain the rotation to 0, 90, 180, and 270 degrees.
 * @property {boolean} [enableRotation=true] Enable rotation.
 * If `false`, a rotation constraint that always sets the rotation to zero is
 * used. The `constrainRotation` option has no effect if `enableRotation` is
 * `false`.
 * @property {import("./extent.js").Extent} [extent] The extent that constrains the
 * view, in other words, nothing outside of this extent can be visible on the map.
 * @property {boolean} [constrainOnlyCenter=false] If true, the extent
 * constraint will only apply to the view center and not the whole extent.
 * @property {boolean} [smoothExtentConstraint=true] If true, the extent
 * constraint will be applied smoothly, i.e. allow the view to go slightly outside
 * of the given `extent`.
 * @property {number} [maxResolution] The maximum resolution used to determine
 * the resolution constraint. It is used together with `minResolution` (or
 * `maxZoom`) and `zoomFactor`. If unspecified it is calculated in such a way
 * that the projection's validity extent fits in a 256x256 px tile. If the
 * projection is Spherical Mercator (the default) then `maxResolution` defaults
 * to `40075016.68557849 / 256 = 156543.03392804097`.
 * @property {number} [minResolution] The minimum resolution used to determine
 * the resolution constraint.  It is used together with `maxResolution` (or
 * `minZoom`) and `zoomFactor`.  If unspecified it is calculated assuming 29
 * zoom levels (with a factor of 2). If the projection is Spherical Mercator
 * (the default) then `minResolution` defaults to
 * `40075016.68557849 / 256 / Math.pow(2, 28) = 0.0005831682455839253`.
 * @property {number} [maxZoom=28] The maximum zoom level used to determine the
 * resolution constraint. It is used together with `minZoom` (or
 * `maxResolution`) and `zoomFactor`.  Note that if `minResolution` is also
 * provided, it is given precedence over `maxZoom`.
 * @property {number} [minZoom=0] The minimum zoom level used to determine the
 * resolution constraint. It is used together with `maxZoom` (or
 * `minResolution`) and `zoomFactor`.  Note that if `maxResolution` is also
 * provided, it is given precedence over `minZoom`.
 * @property {boolean} [multiWorld=false] If `false` the view is constrained so
 * only one world is visible, and you cannot pan off the edge.  If `true` the map
 * may show multiple worlds at low zoom levels.  Only used if the `projection` is
 * global.  Note that if `extent` is also provided it is given precedence.
 * @property {boolean} [constrainResolution=false] If true, the view will always
 * animate to the closest zoom level after an interaction; false means
 * intermediary zoom levels are allowed.
 * @property {boolean} [smoothResolutionConstraint=true] If true, the resolution
 * min/max values will be applied smoothly, i. e. allow the view to exceed slightly
 * the given resolution or zoom bounds.
 * @property {boolean} [showFullExtent=false] Allow the view to be zoomed out to
 * show the full configured extent. By default, when a view is configured with an
 * extent, users will not be able to zoom out so the viewport exceeds the extent in
 * either dimension. This means the full extent may not be visible if the viewport
 * is taller or wider than the aspect ratio of the configured extent. If
 * showFullExtent is true, the user will be able to zoom out so that the viewport
 * exceeds the height or width of the configured extent, but not both, allowing the
 * full extent to be shown.
 * @property {import("./proj.js").ProjectionLike} [projection='EPSG:3857'] The
 * projection. The default is Spherical Mercator.
 * @property {number} [resolution] The initial resolution for the view. The
 * units are `projection` units per pixel (e.g. meters per pixel). An
 * alternative to setting this is to set `zoom`. Layer sources will not be
 * fetched if neither this nor `zoom` are defined, but they can be set later
 * with {@link #setZoom} or {@link #setResolution}.
 * @property {Array<number>} [resolutions] Resolutions that determine the
 * zoom levels if specified. The index in the array corresponds to the zoom level,
 * therefore the resolution values have to be in descending order. It also constrains
 * the resolution by the minimum and maximum value. If set the `maxResolution`,
 * `minResolution`, `minZoom`, `maxZoom`, and `zoomFactor` options are ignored.
 * @property {number} [rotation=0] The initial rotation for the view in radians
 * (positive rotation clockwise, 0 means North).
 * @property {number} [zoom] Only used if `resolution` is not defined. Zoom
 * level used to calculate the initial resolution for the view.
 * @property {number} [zoomFactor=2] The zoom factor used to compute the
 * corresponding resolution.
 * @property {!Array<number>} [padding=[0, 0, 0, 0]] Padding (in css pixels).
 * If the map viewport is partially covered with other content (overlays) along
 * its edges, this setting allows to shift the center of the viewport away from
 * that content. The order of the values is top, right, bottom, left.
 */

/**
 * @typedef {Object} AnimationOptions
 * @property {import("./coordinate.js").Coordinate} [center] The center of the view at the end of
 * the animation.
 * @property {number} [zoom] The zoom level of the view at the end of the
 * animation. This takes precedence over `resolution`.
 * @property {number} [resolution] The resolution of the view at the end
 * of the animation.  If `zoom` is also provided, this option will be ignored.
 * @property {number} [rotation] The rotation of the view at the end of
 * the animation.
 * @property {import("./coordinate.js").Coordinate} [anchor] Optional anchor to remain fixed
 * during a rotation or resolution animation.
 * @property {number} [duration=1000] The duration of the animation in milliseconds.
 * @property {function(number):number} [easing] The easing function used
 * during the animation (defaults to {@link module:ol/easing.inAndOut}).
 * The function will be called for each frame with a number representing a
 * fraction of the animation's duration.  The function should return a number
 * between 0 and 1 representing the progress toward the destination state.
 */

/**
 * @typedef {Object} State
 * @property {import("./coordinate.js").Coordinate} center Center.
 * @property {import("./proj/Projection.js").default} projection Projection.
 * @property {number} resolution Resolution.
 * @property {import("./coordinate.js").Coordinate} [nextCenter] The next center during an animation series.
 * @property {number} [nextResolution] The next resolution during an animation series.
 * @property {number} [nextRotation] The next rotation during an animation series.
 * @property {number} rotation Rotation.
 * @property {number} zoom Zoom.
 */

/**
 * Default min zoom level for the map view.
 * @type {number}
 */
var DEFAULT_MIN_ZOOM = 0;
/**
 * @typedef {import("./ObjectEventType").Types|'change:center'|'change:resolution'|'change:rotation'} ViewObjectEventTypes
 */

/***
 * @template Return
 * @typedef {import("./Observable").OnSignature<import("./Observable").EventTypes, import("./events/Event.js").default, Return> &
 *   import("./Observable").OnSignature<ViewObjectEventTypes, import("./Object").ObjectEvent, Return> &
 *   import("./Observable").CombinedOnSignature<import("./Observable").EventTypes|ViewObjectEventTypes, Return>} ViewOnSignature
 */

/**
 * @classdesc
 * A View object represents a simple 2D view of the map.
 *
 * This is the object to act upon to change the center, resolution,
 * and rotation of the map.
 *
 * A View has a `projection`. The projection determines the
 * coordinate system of the center, and its units determine the units of the
 * resolution (projection units per pixel). The default projection is
 * Web Mercator (EPSG:3857).
 *
 * ### The view states
 *
 * A View is determined by three states: `center`, `resolution`,
 * and `rotation`. Each state has a corresponding getter and setter, e.g.
 * `getCenter` and `setCenter` for the `center` state.
 *
 * The `zoom` state is actually not saved on the view: all computations
 * internally use the `resolution` state. Still, the `setZoom` and `getZoom`
 * methods are available, as well as `getResolutionForZoom` and
 * `getZoomForResolution` to switch from one system to the other.
 *
 * ### The constraints
 *
 * `setCenter`, `setResolution` and `setRotation` can be used to change the
 * states of the view, but any constraint defined in the constructor will
 * be applied along the way.
 *
 * A View object can have a *resolution constraint*, a *rotation constraint*
 * and a *center constraint*.
 *
 * The *resolution constraint* typically restricts min/max values and
 * snaps to specific resolutions. It is determined by the following
 * options: `resolutions`, `maxResolution`, `maxZoom` and `zoomFactor`.
 * If `resolutions` is set, the other three options are ignored. See
 * documentation for each option for more information. By default, the view
 * only has a min/max restriction and allow intermediary zoom levels when
 * pinch-zooming for example.
 *
 * The *rotation constraint* snaps to specific angles. It is determined
 * by the following options: `enableRotation` and `constrainRotation`.
 * By default rotation is allowed and its value is snapped to zero when approaching the
 * horizontal.
 *
 * The *center constraint* is determined by the `extent` option. By
 * default the view center is not constrained at all.
 *
 * ### Changing the view state
 *
 * It is important to note that `setZoom`, `setResolution`, `setCenter` and
 * `setRotation` are subject to the above mentioned constraints. As such, it
 * may sometimes not be possible to know in advance the resulting state of the
 * View. For example, calling `setResolution(10)` does not guarantee that
 * `getResolution()` will return `10`.
 *
 * A consequence of this is that, when applying a delta on the view state, one
 * should use `adjustCenter`, `adjustRotation`, `adjustZoom` and `adjustResolution`
 * rather than the corresponding setters. This will let view do its internal
 * computations. Besides, the `adjust*` methods also take an `opt_anchor`
 * argument which allows specifying an origin for the transformation.
 *
 * ### Interacting with the view
 *
 * View constraints are usually only applied when the view is *at rest*, meaning that
 * no interaction or animation is ongoing. As such, if the user puts the view in a
 * state that is not equivalent to a constrained one (e.g. rotating the view when
 * the snap angle is 0), an animation will be triggered at the interaction end to
 * put back the view to a stable state;
 *
 * @api
 */

var View =
/** @class */
function (_super) {
  __extends(View, _super);
  /**
   * @param {ViewOptions} [opt_options] View options.
   */


  function View(opt_options) {
    var _this = _super.call(this) || this;
    /***
     * @type {ViewOnSignature<import("./events").EventsKey>}
     */


    _this.on;
    /***
     * @type {ViewOnSignature<import("./events").EventsKey>}
     */

    _this.once;
    /***
     * @type {ViewOnSignature<void>}
     */

    _this.un;
    var options = (0, _obj.assign)({}, opt_options);
    /**
     * @private
     * @type {Array<number>}
     */

    _this.hints_ = [0, 0];
    /**
     * @private
     * @type {Array<Array<Animation>>}
     */

    _this.animations_ = [];
    /**
     * @private
     * @type {number|undefined}
     */

    _this.updateAnimationKey_;
    /**
     * @private
     * @const
     * @type {import("./proj/Projection.js").default}
     */

    _this.projection_ = (0, _proj.createProjection)(options.projection, 'EPSG:3857');
    /**
     * @private
     * @type {import("./size.js").Size}
     */

    _this.viewportSize_ = [100, 100];
    /**
     * @private
     * @type {import("./coordinate.js").Coordinate|undefined}
     */

    _this.targetCenter_ = null;
    /**
     * @private
     * @type {number|undefined}
     */

    _this.targetResolution_;
    /**
     * @private
     * @type {number|undefined}
     */

    _this.targetRotation_;
    /**
     * @private
     * @type {import("./coordinate.js").Coordinate}
     */

    _this.nextCenter_ = null;
    /**
     * @private
     * @type {number}
     */

    _this.nextResolution_;
    /**
     * @private
     * @type {number}
     */

    _this.nextRotation_;
    /**
     * @private
     * @type {import("./coordinate.js").Coordinate|undefined}
     */

    _this.cancelAnchor_ = undefined;

    if (options.projection) {
      (0, _proj.disableCoordinateWarning)();
    }

    if (options.center) {
      options.center = (0, _proj.fromUserCoordinate)(options.center, _this.projection_);
    }

    if (options.extent) {
      options.extent = (0, _proj.fromUserExtent)(options.extent, _this.projection_);
    }

    _this.applyOptions_(options);

    return _this;
  }
  /**
   * Set up the view with the given options.
   * @param {ViewOptions} options View options.
   */


  View.prototype.applyOptions_ = function (options) {
    var properties = (0, _obj.assign)({}, options);

    for (var key in _ViewProperty["default"]) {
      delete properties[key];
    }

    this.setProperties(properties, true);
    var resolutionConstraintInfo = createResolutionConstraint(options);
    /**
     * @private
     * @type {number}
     */

    this.maxResolution_ = resolutionConstraintInfo.maxResolution;
    /**
     * @private
     * @type {number}
     */

    this.minResolution_ = resolutionConstraintInfo.minResolution;
    /**
     * @private
     * @type {number}
     */

    this.zoomFactor_ = resolutionConstraintInfo.zoomFactor;
    /**
     * @private
     * @type {Array<number>|undefined}
     */

    this.resolutions_ = options.resolutions;
    /**
     * @type {Array<number>|undefined}
     * @private
     */

    this.padding_ = options.padding;
    /**
     * @private
     * @type {number}
     */

    this.minZoom_ = resolutionConstraintInfo.minZoom;
    var centerConstraint = createCenterConstraint(options);
    var resolutionConstraint = resolutionConstraintInfo.constraint;
    var rotationConstraint = createRotationConstraint(options);
    /**
     * @private
     * @type {Constraints}
     */

    this.constraints_ = {
      center: centerConstraint,
      resolution: resolutionConstraint,
      rotation: rotationConstraint
    };
    this.setRotation(options.rotation !== undefined ? options.rotation : 0);
    this.setCenterInternal(options.center !== undefined ? options.center : null);

    if (options.resolution !== undefined) {
      this.setResolution(options.resolution);
    } else if (options.zoom !== undefined) {
      this.setZoom(options.zoom);
    }
  };

  Object.defineProperty(View.prototype, "padding", {
    /**
     * Padding (in css pixels).
     * If the map viewport is partially covered with other content (overlays) along
     * its edges, this setting allows to shift the center of the viewport away from that
     * content. The order of the values in the array is top, right, bottom, left.
     * The default is no padding, which is equivalent to `[0, 0, 0, 0]`.
     * @type {Array<number>|undefined}
     * @api
     */
    get: function get() {
      return this.padding_;
    },
    set: function set(padding) {
      var oldPadding = this.padding_;
      this.padding_ = padding;
      var center = this.getCenter();

      if (center) {
        var newPadding = padding || [0, 0, 0, 0];
        oldPadding = oldPadding || [0, 0, 0, 0];
        var resolution = this.getResolution();
        var offsetX = resolution / 2 * (newPadding[3] - oldPadding[3] + oldPadding[1] - newPadding[1]);
        var offsetY = resolution / 2 * (newPadding[0] - oldPadding[0] + oldPadding[2] - newPadding[2]);
        this.setCenterInternal([center[0] + offsetX, center[1] - offsetY]);
      }
    },
    enumerable: false,
    configurable: true
  });
  /**
   * Get an updated version of the view options used to construct the view.  The
   * current resolution (or zoom), center, and rotation are applied to any stored
   * options.  The provided options can be used to apply new min/max zoom or
   * resolution limits.
   * @param {ViewOptions} newOptions New options to be applied.
   * @return {ViewOptions} New options updated with the current view state.
   */

  View.prototype.getUpdatedOptions_ = function (newOptions) {
    var options = this.getProperties(); // preserve resolution (or zoom)

    if (options.resolution !== undefined) {
      options.resolution = this.getResolution();
    } else {
      options.zoom = this.getZoom();
    } // preserve center


    options.center = this.getCenterInternal(); // preserve rotation

    options.rotation = this.getRotation();
    return (0, _obj.assign)({}, options, newOptions);
  };
  /**
   * Animate the view.  The view's center, zoom (or resolution), and rotation
   * can be animated for smooth transitions between view states.  For example,
   * to animate the view to a new zoom level:
   *
   *     view.animate({zoom: view.getZoom() + 1});
   *
   * By default, the animation lasts one second and uses in-and-out easing.  You
   * can customize this behavior by including `duration` (in milliseconds) and
   * `easing` options (see {@link module:ol/easing}).
   *
   * To chain together multiple animations, call the method with multiple
   * animation objects.  For example, to first zoom and then pan:
   *
   *     view.animate({zoom: 10}, {center: [0, 0]});
   *
   * If you provide a function as the last argument to the animate method, it
   * will get called at the end of an animation series.  The callback will be
   * called with `true` if the animation series completed on its own or `false`
   * if it was cancelled.
   *
   * Animations are cancelled by user interactions (e.g. dragging the map) or by
   * calling `view.setCenter()`, `view.setResolution()`, or `view.setRotation()`
   * (or another method that calls one of these).
   *
   * @param {...(AnimationOptions|function(boolean): void)} var_args Animation
   *     options.  Multiple animations can be run in series by passing multiple
   *     options objects.  To run multiple animations in parallel, call the method
   *     multiple times.  An optional callback can be provided as a final
   *     argument.  The callback will be called with a boolean indicating whether
   *     the animation completed without being cancelled.
   * @api
   */


  View.prototype.animate = function (var_args) {
    if (this.isDef() && !this.getAnimating()) {
      this.resolveConstraints(0);
    }

    var args = new Array(arguments.length);

    for (var i = 0; i < args.length; ++i) {
      var options = arguments[i];

      if (options.center) {
        options = (0, _obj.assign)({}, options);
        options.center = (0, _proj.fromUserCoordinate)(options.center, this.getProjection());
      }

      if (options.anchor) {
        options = (0, _obj.assign)({}, options);
        options.anchor = (0, _proj.fromUserCoordinate)(options.anchor, this.getProjection());
      }

      args[i] = options;
    }

    this.animateInternal.apply(this, args);
  };
  /**
   * @param {...(AnimationOptions|function(boolean): void)} var_args Animation options.
   */


  View.prototype.animateInternal = function (var_args) {
    var animationCount = arguments.length;
    var callback;

    if (animationCount > 1 && typeof arguments[animationCount - 1] === 'function') {
      callback = arguments[animationCount - 1];
      --animationCount;
    }

    var i = 0;

    for (; i < animationCount && !this.isDef(); ++i) {
      // if view properties are not yet set, shortcut to the final state
      var state = arguments[i];

      if (state.center) {
        this.setCenterInternal(state.center);
      }

      if (state.zoom !== undefined) {
        this.setZoom(state.zoom);
      } else if (state.resolution) {
        this.setResolution(state.resolution);
      }

      if (state.rotation !== undefined) {
        this.setRotation(state.rotation);
      }
    }

    if (i === animationCount) {
      if (callback) {
        animationCallback(callback, true);
      }

      return;
    }

    var start = Date.now();
    var center = this.targetCenter_.slice();
    var resolution = this.targetResolution_;
    var rotation = this.targetRotation_;
    var series = [];

    for (; i < animationCount; ++i) {
      var options =
      /** @type {AnimationOptions} */
      arguments[i];
      var animation = {
        start: start,
        complete: false,
        anchor: options.anchor,
        duration: options.duration !== undefined ? options.duration : 1000,
        easing: options.easing || _easing.inAndOut,
        callback: callback
      };

      if (options.center) {
        animation.sourceCenter = center;
        animation.targetCenter = options.center.slice();
        center = animation.targetCenter;
      }

      if (options.zoom !== undefined) {
        animation.sourceResolution = resolution;
        animation.targetResolution = this.getResolutionForZoom(options.zoom);
        resolution = animation.targetResolution;
      } else if (options.resolution) {
        animation.sourceResolution = resolution;
        animation.targetResolution = options.resolution;
        resolution = animation.targetResolution;
      }

      if (options.rotation !== undefined) {
        animation.sourceRotation = rotation;
        var delta = (0, _math.modulo)(options.rotation - rotation + Math.PI, 2 * Math.PI) - Math.PI;
        animation.targetRotation = rotation + delta;
        rotation = animation.targetRotation;
      } // check if animation is a no-op


      if (isNoopAnimation(animation)) {
        animation.complete = true; // we still push it onto the series for callback handling
      } else {
        start += animation.duration;
      }

      series.push(animation);
    }

    this.animations_.push(series);
    this.setHint(_ViewHint["default"].ANIMATING, 1);
    this.updateAnimations_();
  };
  /**
   * Determine if the view is being animated.
   * @return {boolean} The view is being animated.
   * @api
   */


  View.prototype.getAnimating = function () {
    return this.hints_[_ViewHint["default"].ANIMATING] > 0;
  };
  /**
   * Determine if the user is interacting with the view, such as panning or zooming.
   * @return {boolean} The view is being interacted with.
   * @api
   */


  View.prototype.getInteracting = function () {
    return this.hints_[_ViewHint["default"].INTERACTING] > 0;
  };
  /**
   * Cancel any ongoing animations.
   * @api
   */


  View.prototype.cancelAnimations = function () {
    this.setHint(_ViewHint["default"].ANIMATING, -this.hints_[_ViewHint["default"].ANIMATING]);
    var anchor;

    for (var i = 0, ii = this.animations_.length; i < ii; ++i) {
      var series = this.animations_[i];

      if (series[0].callback) {
        animationCallback(series[0].callback, false);
      }

      if (!anchor) {
        for (var j = 0, jj = series.length; j < jj; ++j) {
          var animation = series[j];

          if (!animation.complete) {
            anchor = animation.anchor;
            break;
          }
        }
      }
    }

    this.animations_.length = 0;
    this.cancelAnchor_ = anchor;
    this.nextCenter_ = null;
    this.nextResolution_ = NaN;
    this.nextRotation_ = NaN;
  };
  /**
   * Update all animations.
   */


  View.prototype.updateAnimations_ = function () {
    if (this.updateAnimationKey_ !== undefined) {
      cancelAnimationFrame(this.updateAnimationKey_);
      this.updateAnimationKey_ = undefined;
    }

    if (!this.getAnimating()) {
      return;
    }

    var now = Date.now();
    var more = false;

    for (var i = this.animations_.length - 1; i >= 0; --i) {
      var series = this.animations_[i];
      var seriesComplete = true;

      for (var j = 0, jj = series.length; j < jj; ++j) {
        var animation = series[j];

        if (animation.complete) {
          continue;
        }

        var elapsed = now - animation.start;
        var fraction = animation.duration > 0 ? elapsed / animation.duration : 1;

        if (fraction >= 1) {
          animation.complete = true;
          fraction = 1;
        } else {
          seriesComplete = false;
        }

        var progress = animation.easing(fraction);

        if (animation.sourceCenter) {
          var x0 = animation.sourceCenter[0];
          var y0 = animation.sourceCenter[1];
          var x1 = animation.targetCenter[0];
          var y1 = animation.targetCenter[1];
          this.nextCenter_ = animation.targetCenter;
          var x = x0 + progress * (x1 - x0);
          var y = y0 + progress * (y1 - y0);
          this.targetCenter_ = [x, y];
        }

        if (animation.sourceResolution && animation.targetResolution) {
          var resolution = progress === 1 ? animation.targetResolution : animation.sourceResolution + progress * (animation.targetResolution - animation.sourceResolution);

          if (animation.anchor) {
            var size = this.getViewportSize_(this.getRotation());
            var constrainedResolution = this.constraints_.resolution(resolution, 0, size, true);
            this.targetCenter_ = this.calculateCenterZoom(constrainedResolution, animation.anchor);
          }

          this.nextResolution_ = animation.targetResolution;
          this.targetResolution_ = resolution;
          this.applyTargetState_(true);
        }

        if (animation.sourceRotation !== undefined && animation.targetRotation !== undefined) {
          var rotation = progress === 1 ? (0, _math.modulo)(animation.targetRotation + Math.PI, 2 * Math.PI) - Math.PI : animation.sourceRotation + progress * (animation.targetRotation - animation.sourceRotation);

          if (animation.anchor) {
            var constrainedRotation = this.constraints_.rotation(rotation, true);
            this.targetCenter_ = this.calculateCenterRotate(constrainedRotation, animation.anchor);
          }

          this.nextRotation_ = animation.targetRotation;
          this.targetRotation_ = rotation;
        }

        this.applyTargetState_(true);
        more = true;

        if (!animation.complete) {
          break;
        }
      }

      if (seriesComplete) {
        this.animations_[i] = null;
        this.setHint(_ViewHint["default"].ANIMATING, -1);
        this.nextCenter_ = null;
        this.nextResolution_ = NaN;
        this.nextRotation_ = NaN;
        var callback = series[0].callback;

        if (callback) {
          animationCallback(callback, true);
        }
      }
    } // prune completed series


    this.animations_ = this.animations_.filter(Boolean);

    if (more && this.updateAnimationKey_ === undefined) {
      this.updateAnimationKey_ = requestAnimationFrame(this.updateAnimations_.bind(this));
    }
  };
  /**
   * @param {number} rotation Target rotation.
   * @param {import("./coordinate.js").Coordinate} anchor Rotation anchor.
   * @return {import("./coordinate.js").Coordinate|undefined} Center for rotation and anchor.
   */


  View.prototype.calculateCenterRotate = function (rotation, anchor) {
    var center;
    var currentCenter = this.getCenterInternal();

    if (currentCenter !== undefined) {
      center = [currentCenter[0] - anchor[0], currentCenter[1] - anchor[1]];
      (0, _coordinate.rotate)(center, rotation - this.getRotation());
      (0, _coordinate.add)(center, anchor);
    }

    return center;
  };
  /**
   * @param {number} resolution Target resolution.
   * @param {import("./coordinate.js").Coordinate} anchor Zoom anchor.
   * @return {import("./coordinate.js").Coordinate|undefined} Center for resolution and anchor.
   */


  View.prototype.calculateCenterZoom = function (resolution, anchor) {
    var center;
    var currentCenter = this.getCenterInternal();
    var currentResolution = this.getResolution();

    if (currentCenter !== undefined && currentResolution !== undefined) {
      var x = anchor[0] - resolution * (anchor[0] - currentCenter[0]) / currentResolution;
      var y = anchor[1] - resolution * (anchor[1] - currentCenter[1]) / currentResolution;
      center = [x, y];
    }

    return center;
  };
  /**
   * Returns the current viewport size.
   * @private
   * @param {number} [opt_rotation] Take into account the rotation of the viewport when giving the size
   * @return {import("./size.js").Size} Viewport size or `[100, 100]` when no viewport is found.
   */


  View.prototype.getViewportSize_ = function (opt_rotation) {
    var size = this.viewportSize_;

    if (opt_rotation) {
      var w = size[0];
      var h = size[1];
      return [Math.abs(w * Math.cos(opt_rotation)) + Math.abs(h * Math.sin(opt_rotation)), Math.abs(w * Math.sin(opt_rotation)) + Math.abs(h * Math.cos(opt_rotation))];
    } else {
      return size;
    }
  };
  /**
   * Stores the viewport size on the view. The viewport size is not read every time from the DOM
   * to avoid performance hit and layout reflow.
   * This should be done on map size change.
   * Note: the constraints are not resolved during an animation to avoid stopping it
   * @param {import("./size.js").Size} [opt_size] Viewport size; if undefined, [100, 100] is assumed
   */


  View.prototype.setViewportSize = function (opt_size) {
    this.viewportSize_ = Array.isArray(opt_size) ? opt_size.slice() : [100, 100];

    if (!this.getAnimating()) {
      this.resolveConstraints(0);
    }
  };
  /**
   * Get the view center.
   * @return {import("./coordinate.js").Coordinate|undefined} The center of the view.
   * @observable
   * @api
   */


  View.prototype.getCenter = function () {
    var center = this.getCenterInternal();

    if (!center) {
      return center;
    }

    return (0, _proj.toUserCoordinate)(center, this.getProjection());
  };
  /**
   * Get the view center without transforming to user projection.
   * @return {import("./coordinate.js").Coordinate|undefined} The center of the view.
   */


  View.prototype.getCenterInternal = function () {
    return (
      /** @type {import("./coordinate.js").Coordinate|undefined} */
      this.get(_ViewProperty["default"].CENTER)
    );
  };
  /**
   * @return {Constraints} Constraints.
   */


  View.prototype.getConstraints = function () {
    return this.constraints_;
  };
  /**
   * @return {boolean} Resolution constraint is set
   */


  View.prototype.getConstrainResolution = function () {
    return this.get('constrainResolution');
  };
  /**
   * @param {Array<number>} [opt_hints] Destination array.
   * @return {Array<number>} Hint.
   */


  View.prototype.getHints = function (opt_hints) {
    if (opt_hints !== undefined) {
      opt_hints[0] = this.hints_[0];
      opt_hints[1] = this.hints_[1];
      return opt_hints;
    } else {
      return this.hints_.slice();
    }
  };
  /**
   * Calculate the extent for the current view state and the passed size.
   * The size is the pixel dimensions of the box into which the calculated extent
   * should fit. In most cases you want to get the extent of the entire map,
   * that is `map.getSize()`.
   * @param {import("./size.js").Size} [opt_size] Box pixel size. If not provided, the size
   * of the map that uses this view will be used.
   * @return {import("./extent.js").Extent} Extent.
   * @api
   */


  View.prototype.calculateExtent = function (opt_size) {
    var extent = this.calculateExtentInternal(opt_size);
    return (0, _proj.toUserExtent)(extent, this.getProjection());
  };
  /**
   * @param {import("./size.js").Size} [opt_size] Box pixel size. If not provided,
   * the map's last known viewport size will be used.
   * @return {import("./extent.js").Extent} Extent.
   */


  View.prototype.calculateExtentInternal = function (opt_size) {
    var size = opt_size || this.getViewportSizeMinusPadding_();
    var center =
    /** @type {!import("./coordinate.js").Coordinate} */
    this.getCenterInternal();
    (0, _asserts.assert)(center, 1); // The view center is not defined

    var resolution =
    /** @type {!number} */
    this.getResolution();
    (0, _asserts.assert)(resolution !== undefined, 2); // The view resolution is not defined

    var rotation =
    /** @type {!number} */
    this.getRotation();
    (0, _asserts.assert)(rotation !== undefined, 3); // The view rotation is not defined

    return (0, _extent.getForViewAndSize)(center, resolution, rotation, size);
  };
  /**
   * Get the maximum resolution of the view.
   * @return {number} The maximum resolution of the view.
   * @api
   */


  View.prototype.getMaxResolution = function () {
    return this.maxResolution_;
  };
  /**
   * Get the minimum resolution of the view.
   * @return {number} The minimum resolution of the view.
   * @api
   */


  View.prototype.getMinResolution = function () {
    return this.minResolution_;
  };
  /**
   * Get the maximum zoom level for the view.
   * @return {number} The maximum zoom level.
   * @api
   */


  View.prototype.getMaxZoom = function () {
    return (
      /** @type {number} */
      this.getZoomForResolution(this.minResolution_)
    );
  };
  /**
   * Set a new maximum zoom level for the view.
   * @param {number} zoom The maximum zoom level.
   * @api
   */


  View.prototype.setMaxZoom = function (zoom) {
    this.applyOptions_(this.getUpdatedOptions_({
      maxZoom: zoom
    }));
  };
  /**
   * Get the minimum zoom level for the view.
   * @return {number} The minimum zoom level.
   * @api
   */


  View.prototype.getMinZoom = function () {
    return (
      /** @type {number} */
      this.getZoomForResolution(this.maxResolution_)
    );
  };
  /**
   * Set a new minimum zoom level for the view.
   * @param {number} zoom The minimum zoom level.
   * @api
   */


  View.prototype.setMinZoom = function (zoom) {
    this.applyOptions_(this.getUpdatedOptions_({
      minZoom: zoom
    }));
  };
  /**
   * Set whether the view should allow intermediary zoom levels.
   * @param {boolean} enabled Whether the resolution is constrained.
   * @api
   */


  View.prototype.setConstrainResolution = function (enabled) {
    this.applyOptions_(this.getUpdatedOptions_({
      constrainResolution: enabled
    }));
  };
  /**
   * Get the view projection.
   * @return {import("./proj/Projection.js").default} The projection of the view.
   * @api
   */


  View.prototype.getProjection = function () {
    return this.projection_;
  };
  /**
   * Get the view resolution.
   * @return {number|undefined} The resolution of the view.
   * @observable
   * @api
   */


  View.prototype.getResolution = function () {
    return (
      /** @type {number|undefined} */
      this.get(_ViewProperty["default"].RESOLUTION)
    );
  };
  /**
   * Get the resolutions for the view. This returns the array of resolutions
   * passed to the constructor of the View, or undefined if none were given.
   * @return {Array<number>|undefined} The resolutions of the view.
   * @api
   */


  View.prototype.getResolutions = function () {
    return this.resolutions_;
  };
  /**
   * Get the resolution for a provided extent (in map units) and size (in pixels).
   * @param {import("./extent.js").Extent} extent Extent.
   * @param {import("./size.js").Size} [opt_size] Box pixel size.
   * @return {number} The resolution at which the provided extent will render at
   *     the given size.
   * @api
   */


  View.prototype.getResolutionForExtent = function (extent, opt_size) {
    return this.getResolutionForExtentInternal((0, _proj.fromUserExtent)(extent, this.getProjection()), opt_size);
  };
  /**
   * Get the resolution for a provided extent (in map units) and size (in pixels).
   * @param {import("./extent.js").Extent} extent Extent.
   * @param {import("./size.js").Size} [opt_size] Box pixel size.
   * @return {number} The resolution at which the provided extent will render at
   *     the given size.
   */


  View.prototype.getResolutionForExtentInternal = function (extent, opt_size) {
    var size = opt_size || this.getViewportSizeMinusPadding_();
    var xResolution = (0, _extent.getWidth)(extent) / size[0];
    var yResolution = (0, _extent.getHeight)(extent) / size[1];
    return Math.max(xResolution, yResolution);
  };
  /**
   * Return a function that returns a value between 0 and 1 for a
   * resolution. Exponential scaling is assumed.
   * @param {number} [opt_power] Power.
   * @return {function(number): number} Resolution for value function.
   */


  View.prototype.getResolutionForValueFunction = function (opt_power) {
    var power = opt_power || 2;
    var maxResolution = this.getConstrainedResolution(this.maxResolution_);
    var minResolution = this.minResolution_;
    var max = Math.log(maxResolution / minResolution) / Math.log(power);
    return (
      /**
       * @param {number} value Value.
       * @return {number} Resolution.
       */
      function (value) {
        var resolution = maxResolution / Math.pow(power, value * max);
        return resolution;
      }
    );
  };
  /**
   * Get the view rotation.
   * @return {number} The rotation of the view in radians.
   * @observable
   * @api
   */


  View.prototype.getRotation = function () {
    return (
      /** @type {number} */
      this.get(_ViewProperty["default"].ROTATION)
    );
  };
  /**
   * Return a function that returns a resolution for a value between
   * 0 and 1. Exponential scaling is assumed.
   * @param {number} [opt_power] Power.
   * @return {function(number): number} Value for resolution function.
   */


  View.prototype.getValueForResolutionFunction = function (opt_power) {
    var logPower = Math.log(opt_power || 2);
    var maxResolution = this.getConstrainedResolution(this.maxResolution_);
    var minResolution = this.minResolution_;
    var max = Math.log(maxResolution / minResolution) / logPower;
    return (
      /**
       * @param {number} resolution Resolution.
       * @return {number} Value.
       */
      function (resolution) {
        var value = Math.log(maxResolution / resolution) / logPower / max;
        return value;
      }
    );
  };
  /**
   * Returns the size of the viewport minus padding.
   * @private
   * @param {number} [opt_rotation] Take into account the rotation of the viewport when giving the size
   * @return {import("./size.js").Size} Viewport size reduced by the padding.
   */


  View.prototype.getViewportSizeMinusPadding_ = function (opt_rotation) {
    var size = this.getViewportSize_(opt_rotation);
    var padding = this.padding_;

    if (padding) {
      size = [size[0] - padding[1] - padding[3], size[1] - padding[0] - padding[2]];
    }

    return size;
  };
  /**
   * @return {State} View state.
   */


  View.prototype.getState = function () {
    var projection = this.getProjection();
    var resolution = this.getResolution();
    var rotation = this.getRotation();
    var center =
    /** @type {import("./coordinate.js").Coordinate} */
    this.getCenterInternal();
    var padding = this.padding_;

    if (padding) {
      var reducedSize = this.getViewportSizeMinusPadding_();
      center = calculateCenterOn(center, this.getViewportSize_(), [reducedSize[0] / 2 + padding[3], reducedSize[1] / 2 + padding[0]], resolution, rotation);
    }

    return {
      center: center.slice(0),
      projection: projection !== undefined ? projection : null,
      resolution: resolution,
      nextCenter: this.nextCenter_,
      nextResolution: this.nextResolution_,
      nextRotation: this.nextRotation_,
      rotation: rotation,
      zoom: this.getZoom()
    };
  };
  /**
   * Get the current zoom level. This method may return non-integer zoom levels
   * if the view does not constrain the resolution, or if an interaction or
   * animation is underway.
   * @return {number|undefined} Zoom.
   * @api
   */


  View.prototype.getZoom = function () {
    var zoom;
    var resolution = this.getResolution();

    if (resolution !== undefined) {
      zoom = this.getZoomForResolution(resolution);
    }

    return zoom;
  };
  /**
   * Get the zoom level for a resolution.
   * @param {number} resolution The resolution.
   * @return {number|undefined} The zoom level for the provided resolution.
   * @api
   */


  View.prototype.getZoomForResolution = function (resolution) {
    var offset = this.minZoom_ || 0;
    var max, zoomFactor;

    if (this.resolutions_) {
      var nearest = (0, _array.linearFindNearest)(this.resolutions_, resolution, 1);
      offset = nearest;
      max = this.resolutions_[nearest];

      if (nearest == this.resolutions_.length - 1) {
        zoomFactor = 2;
      } else {
        zoomFactor = max / this.resolutions_[nearest + 1];
      }
    } else {
      max = this.maxResolution_;
      zoomFactor = this.zoomFactor_;
    }

    return offset + Math.log(max / resolution) / Math.log(zoomFactor);
  };
  /**
   * Get the resolution for a zoom level.
   * @param {number} zoom Zoom level.
   * @return {number} The view resolution for the provided zoom level.
   * @api
   */


  View.prototype.getResolutionForZoom = function (zoom) {
    if (this.resolutions_) {
      if (this.resolutions_.length <= 1) {
        return 0;
      }

      var baseLevel = (0, _math.clamp)(Math.floor(zoom), 0, this.resolutions_.length - 2);
      var zoomFactor = this.resolutions_[baseLevel] / this.resolutions_[baseLevel + 1];
      return this.resolutions_[baseLevel] / Math.pow(zoomFactor, (0, _math.clamp)(zoom - baseLevel, 0, 1));
    } else {
      return this.maxResolution_ / Math.pow(this.zoomFactor_, zoom - this.minZoom_);
    }
  };
  /**
   * Fit the given geometry or extent based on the given map size and border.
   * The size is pixel dimensions of the box to fit the extent into.
   * In most cases you will want to use the map size, that is `map.getSize()`.
   * Takes care of the map angle.
   * @param {import("./geom/SimpleGeometry.js").default|import("./extent.js").Extent} geometryOrExtent The geometry or
   *     extent to fit the view to.
   * @param {FitOptions} [opt_options] Options.
   * @api
   */


  View.prototype.fit = function (geometryOrExtent, opt_options) {
    /** @type {import("./geom/SimpleGeometry.js").default} */
    var geometry;
    (0, _asserts.assert)(Array.isArray(geometryOrExtent) || typeof
    /** @type {?} */
    geometryOrExtent.getSimplifiedGeometry === 'function', 24); // Invalid extent or geometry provided as `geometry`

    if (Array.isArray(geometryOrExtent)) {
      (0, _asserts.assert)(!(0, _extent.isEmpty)(geometryOrExtent), 25); // Cannot fit empty extent provided as `geometry`

      var extent = (0, _proj.fromUserExtent)(geometryOrExtent, this.getProjection());
      geometry = (0, _Polygon.fromExtent)(extent);
    } else if (geometryOrExtent.getType() === _GeometryType["default"].CIRCLE) {
      var extent = (0, _proj.fromUserExtent)(geometryOrExtent.getExtent(), this.getProjection());
      geometry = (0, _Polygon.fromExtent)(extent);
      geometry.rotate(this.getRotation(), (0, _extent.getCenter)(extent));
    } else {
      var userProjection = (0, _proj.getUserProjection)();

      if (userProjection) {
        geometry =
        /** @type {import("./geom/SimpleGeometry.js").default} */
        geometryOrExtent.clone().transform(userProjection, this.getProjection());
      } else {
        geometry = geometryOrExtent;
      }
    }

    this.fitInternal(geometry, opt_options);
  };
  /**
   * Calculate rotated extent
   * @param {import("./geom/SimpleGeometry.js").default} geometry The geometry.
   * @return {import("./extent").Extent} The rotated extent for the geometry.
   */


  View.prototype.rotatedExtentForGeometry = function (geometry) {
    var rotation = this.getRotation();
    var cosAngle = Math.cos(rotation);
    var sinAngle = Math.sin(-rotation);
    var coords = geometry.getFlatCoordinates();
    var stride = geometry.getStride();
    var minRotX = +Infinity;
    var minRotY = +Infinity;
    var maxRotX = -Infinity;
    var maxRotY = -Infinity;

    for (var i = 0, ii = coords.length; i < ii; i += stride) {
      var rotX = coords[i] * cosAngle - coords[i + 1] * sinAngle;
      var rotY = coords[i] * sinAngle + coords[i + 1] * cosAngle;
      minRotX = Math.min(minRotX, rotX);
      minRotY = Math.min(minRotY, rotY);
      maxRotX = Math.max(maxRotX, rotX);
      maxRotY = Math.max(maxRotY, rotY);
    }

    return [minRotX, minRotY, maxRotX, maxRotY];
  };
  /**
   * @param {import("./geom/SimpleGeometry.js").default} geometry The geometry.
   * @param {FitOptions} [opt_options] Options.
   */


  View.prototype.fitInternal = function (geometry, opt_options) {
    var options = opt_options || {};
    var size = options.size;

    if (!size) {
      size = this.getViewportSizeMinusPadding_();
    }

    var padding = options.padding !== undefined ? options.padding : [0, 0, 0, 0];
    var nearest = options.nearest !== undefined ? options.nearest : false;
    var minResolution;

    if (options.minResolution !== undefined) {
      minResolution = options.minResolution;
    } else if (options.maxZoom !== undefined) {
      minResolution = this.getResolutionForZoom(options.maxZoom);
    } else {
      minResolution = 0;
    }

    var rotatedExtent = this.rotatedExtentForGeometry(geometry); // calculate resolution

    var resolution = this.getResolutionForExtentInternal(rotatedExtent, [size[0] - padding[1] - padding[3], size[1] - padding[0] - padding[2]]);
    resolution = isNaN(resolution) ? minResolution : Math.max(resolution, minResolution);
    resolution = this.getConstrainedResolution(resolution, nearest ? 0 : 1); // calculate center

    var rotation = this.getRotation();
    var sinAngle = Math.sin(rotation);
    var cosAngle = Math.cos(rotation);
    var centerRot = (0, _extent.getCenter)(rotatedExtent);
    centerRot[0] += (padding[1] - padding[3]) / 2 * resolution;
    centerRot[1] += (padding[0] - padding[2]) / 2 * resolution;
    var centerX = centerRot[0] * cosAngle - centerRot[1] * sinAngle;
    var centerY = centerRot[1] * cosAngle + centerRot[0] * sinAngle;
    var center = this.getConstrainedCenter([centerX, centerY], resolution);
    var callback = options.callback ? options.callback : _functions.VOID;

    if (options.duration !== undefined) {
      this.animateInternal({
        resolution: resolution,
        center: center,
        duration: options.duration,
        easing: options.easing
      }, callback);
    } else {
      this.targetResolution_ = resolution;
      this.targetCenter_ = center;
      this.applyTargetState_(false, true);
      animationCallback(callback, true);
    }
  };
  /**
   * Center on coordinate and view position.
   * @param {import("./coordinate.js").Coordinate} coordinate Coordinate.
   * @param {import("./size.js").Size} size Box pixel size.
   * @param {import("./pixel.js").Pixel} position Position on the view to center on.
   * @api
   */


  View.prototype.centerOn = function (coordinate, size, position) {
    this.centerOnInternal((0, _proj.fromUserCoordinate)(coordinate, this.getProjection()), size, position);
  };
  /**
   * @param {import("./coordinate.js").Coordinate} coordinate Coordinate.
   * @param {import("./size.js").Size} size Box pixel size.
   * @param {import("./pixel.js").Pixel} position Position on the view to center on.
   */


  View.prototype.centerOnInternal = function (coordinate, size, position) {
    this.setCenterInternal(calculateCenterOn(coordinate, size, position, this.getResolution(), this.getRotation()));
  };
  /**
   * Calculates the shift between map and viewport center.
   * @param {import("./coordinate.js").Coordinate} center Center.
   * @param {number} resolution Resolution.
   * @param {number} rotation Rotation.
   * @param {import("./size.js").Size} size Size.
   * @return {Array<number>|undefined} Center shift.
   */


  View.prototype.calculateCenterShift = function (center, resolution, rotation, size) {
    var centerShift;
    var padding = this.padding_;

    if (padding && center) {
      var reducedSize = this.getViewportSizeMinusPadding_(-rotation);
      var shiftedCenter = calculateCenterOn(center, size, [reducedSize[0] / 2 + padding[3], reducedSize[1] / 2 + padding[0]], resolution, rotation);
      centerShift = [center[0] - shiftedCenter[0], center[1] - shiftedCenter[1]];
    }

    return centerShift;
  };
  /**
   * @return {boolean} Is defined.
   */


  View.prototype.isDef = function () {
    return !!this.getCenterInternal() && this.getResolution() !== undefined;
  };
  /**
   * Adds relative coordinates to the center of the view. Any extent constraint will apply.
   * @param {import("./coordinate.js").Coordinate} deltaCoordinates Relative value to add.
   * @api
   */


  View.prototype.adjustCenter = function (deltaCoordinates) {
    var center = (0, _proj.toUserCoordinate)(this.targetCenter_, this.getProjection());
    this.setCenter([center[0] + deltaCoordinates[0], center[1] + deltaCoordinates[1]]);
  };
  /**
   * Adds relative coordinates to the center of the view. Any extent constraint will apply.
   * @param {import("./coordinate.js").Coordinate} deltaCoordinates Relative value to add.
   */


  View.prototype.adjustCenterInternal = function (deltaCoordinates) {
    var center = this.targetCenter_;
    this.setCenterInternal([center[0] + deltaCoordinates[0], center[1] + deltaCoordinates[1]]);
  };
  /**
   * Multiply the view resolution by a ratio, optionally using an anchor. Any resolution
   * constraint will apply.
   * @param {number} ratio The ratio to apply on the view resolution.
   * @param {import("./coordinate.js").Coordinate} [opt_anchor] The origin of the transformation.
   * @api
   */


  View.prototype.adjustResolution = function (ratio, opt_anchor) {
    var anchor = opt_anchor && (0, _proj.fromUserCoordinate)(opt_anchor, this.getProjection());
    this.adjustResolutionInternal(ratio, anchor);
  };
  /**
   * Multiply the view resolution by a ratio, optionally using an anchor. Any resolution
   * constraint will apply.
   * @param {number} ratio The ratio to apply on the view resolution.
   * @param {import("./coordinate.js").Coordinate} [opt_anchor] The origin of the transformation.
   */


  View.prototype.adjustResolutionInternal = function (ratio, opt_anchor) {
    var isMoving = this.getAnimating() || this.getInteracting();
    var size = this.getViewportSize_(this.getRotation());
    var newResolution = this.constraints_.resolution(this.targetResolution_ * ratio, 0, size, isMoving);

    if (opt_anchor) {
      this.targetCenter_ = this.calculateCenterZoom(newResolution, opt_anchor);
    }

    this.targetResolution_ *= ratio;
    this.applyTargetState_();
  };
  /**
   * Adds a value to the view zoom level, optionally using an anchor. Any resolution
   * constraint will apply.
   * @param {number} delta Relative value to add to the zoom level.
   * @param {import("./coordinate.js").Coordinate} [opt_anchor] The origin of the transformation.
   * @api
   */


  View.prototype.adjustZoom = function (delta, opt_anchor) {
    this.adjustResolution(Math.pow(this.zoomFactor_, -delta), opt_anchor);
  };
  /**
   * Adds a value to the view rotation, optionally using an anchor. Any rotation
   * constraint will apply.
   * @param {number} delta Relative value to add to the zoom rotation, in radians.
   * @param {import("./coordinate.js").Coordinate} [opt_anchor] The rotation center.
   * @api
   */


  View.prototype.adjustRotation = function (delta, opt_anchor) {
    if (opt_anchor) {
      opt_anchor = (0, _proj.fromUserCoordinate)(opt_anchor, this.getProjection());
    }

    this.adjustRotationInternal(delta, opt_anchor);
  };
  /**
   * @param {number} delta Relative value to add to the zoom rotation, in radians.
   * @param {import("./coordinate.js").Coordinate} [opt_anchor] The rotation center.
   */


  View.prototype.adjustRotationInternal = function (delta, opt_anchor) {
    var isMoving = this.getAnimating() || this.getInteracting();
    var newRotation = this.constraints_.rotation(this.targetRotation_ + delta, isMoving);

    if (opt_anchor) {
      this.targetCenter_ = this.calculateCenterRotate(newRotation, opt_anchor);
    }

    this.targetRotation_ += delta;
    this.applyTargetState_();
  };
  /**
   * Set the center of the current view. Any extent constraint will apply.
   * @param {import("./coordinate.js").Coordinate|undefined} center The center of the view.
   * @observable
   * @api
   */


  View.prototype.setCenter = function (center) {
    this.setCenterInternal(center ? (0, _proj.fromUserCoordinate)(center, this.getProjection()) : center);
  };
  /**
   * Set the center using the view projection (not the user projection).
   * @param {import("./coordinate.js").Coordinate|undefined} center The center of the view.
   */


  View.prototype.setCenterInternal = function (center) {
    this.targetCenter_ = center;
    this.applyTargetState_();
  };
  /**
   * @param {import("./ViewHint.js").default} hint Hint.
   * @param {number} delta Delta.
   * @return {number} New value.
   */


  View.prototype.setHint = function (hint, delta) {
    this.hints_[hint] += delta;
    this.changed();
    return this.hints_[hint];
  };
  /**
   * Set the resolution for this view. Any resolution constraint will apply.
   * @param {number|undefined} resolution The resolution of the view.
   * @observable
   * @api
   */


  View.prototype.setResolution = function (resolution) {
    this.targetResolution_ = resolution;
    this.applyTargetState_();
  };
  /**
   * Set the rotation for this view. Any rotation constraint will apply.
   * @param {number} rotation The rotation of the view in radians.
   * @observable
   * @api
   */


  View.prototype.setRotation = function (rotation) {
    this.targetRotation_ = rotation;
    this.applyTargetState_();
  };
  /**
   * Zoom to a specific zoom level. Any resolution constrain will apply.
   * @param {number} zoom Zoom level.
   * @api
   */


  View.prototype.setZoom = function (zoom) {
    this.setResolution(this.getResolutionForZoom(zoom));
  };
  /**
   * Recompute rotation/resolution/center based on target values.
   * Note: we have to compute rotation first, then resolution and center considering that
   * parameters can influence one another in case a view extent constraint is present.
   * @param {boolean} [opt_doNotCancelAnims] Do not cancel animations.
   * @param {boolean} [opt_forceMoving] Apply constraints as if the view is moving.
   * @private
   */


  View.prototype.applyTargetState_ = function (opt_doNotCancelAnims, opt_forceMoving) {
    var isMoving = this.getAnimating() || this.getInteracting() || opt_forceMoving; // compute rotation

    var newRotation = this.constraints_.rotation(this.targetRotation_, isMoving);
    var size = this.getViewportSize_(newRotation);
    var newResolution = this.constraints_.resolution(this.targetResolution_, 0, size, isMoving);
    var newCenter = this.constraints_.center(this.targetCenter_, newResolution, size, isMoving, this.calculateCenterShift(this.targetCenter_, newResolution, newRotation, size));

    if (this.get(_ViewProperty["default"].ROTATION) !== newRotation) {
      this.set(_ViewProperty["default"].ROTATION, newRotation);
    }

    if (this.get(_ViewProperty["default"].RESOLUTION) !== newResolution) {
      this.set(_ViewProperty["default"].RESOLUTION, newResolution);
      this.set('zoom', this.getZoom(), true);
    }

    if (!newCenter || !this.get(_ViewProperty["default"].CENTER) || !(0, _coordinate.equals)(this.get(_ViewProperty["default"].CENTER), newCenter)) {
      this.set(_ViewProperty["default"].CENTER, newCenter);
    }

    if (this.getAnimating() && !opt_doNotCancelAnims) {
      this.cancelAnimations();
    }

    this.cancelAnchor_ = undefined;
  };
  /**
   * If any constraints need to be applied, an animation will be triggered.
   * This is typically done on interaction end.
   * Note: calling this with a duration of 0 will apply the constrained values straight away,
   * without animation.
   * @param {number} [opt_duration] The animation duration in ms.
   * @param {number} [opt_resolutionDirection] Which direction to zoom.
   * @param {import("./coordinate.js").Coordinate} [opt_anchor] The origin of the transformation.
   */


  View.prototype.resolveConstraints = function (opt_duration, opt_resolutionDirection, opt_anchor) {
    var duration = opt_duration !== undefined ? opt_duration : 200;
    var direction = opt_resolutionDirection || 0;
    var newRotation = this.constraints_.rotation(this.targetRotation_);
    var size = this.getViewportSize_(newRotation);
    var newResolution = this.constraints_.resolution(this.targetResolution_, direction, size);
    var newCenter = this.constraints_.center(this.targetCenter_, newResolution, size, false, this.calculateCenterShift(this.targetCenter_, newResolution, newRotation, size));

    if (duration === 0 && !this.cancelAnchor_) {
      this.targetResolution_ = newResolution;
      this.targetRotation_ = newRotation;
      this.targetCenter_ = newCenter;
      this.applyTargetState_();
      return;
    }

    var anchor = opt_anchor || (duration === 0 ? this.cancelAnchor_ : undefined);
    this.cancelAnchor_ = undefined;

    if (this.getResolution() !== newResolution || this.getRotation() !== newRotation || !this.getCenterInternal() || !(0, _coordinate.equals)(this.getCenterInternal(), newCenter)) {
      if (this.getAnimating()) {
        this.cancelAnimations();
      }

      this.animateInternal({
        rotation: newRotation,
        center: newCenter,
        resolution: newResolution,
        duration: duration,
        easing: _easing.easeOut,
        anchor: anchor
      });
    }
  };
  /**
   * Notify the View that an interaction has started.
   * The view state will be resolved to a stable one if needed
   * (depending on its constraints).
   * @api
   */


  View.prototype.beginInteraction = function () {
    this.resolveConstraints(0);
    this.setHint(_ViewHint["default"].INTERACTING, 1);
  };
  /**
   * Notify the View that an interaction has ended. The view state will be resolved
   * to a stable one if needed (depending on its constraints).
   * @param {number} [opt_duration] Animation duration in ms.
   * @param {number} [opt_resolutionDirection] Which direction to zoom.
   * @param {import("./coordinate.js").Coordinate} [opt_anchor] The origin of the transformation.
   * @api
   */


  View.prototype.endInteraction = function (opt_duration, opt_resolutionDirection, opt_anchor) {
    var anchor = opt_anchor && (0, _proj.fromUserCoordinate)(opt_anchor, this.getProjection());
    this.endInteractionInternal(opt_duration, opt_resolutionDirection, anchor);
  };
  /**
   * Notify the View that an interaction has ended. The view state will be resolved
   * to a stable one if needed (depending on its constraints).
   * @param {number} [opt_duration] Animation duration in ms.
   * @param {number} [opt_resolutionDirection] Which direction to zoom.
   * @param {import("./coordinate.js").Coordinate} [opt_anchor] The origin of the transformation.
   */


  View.prototype.endInteractionInternal = function (opt_duration, opt_resolutionDirection, opt_anchor) {
    this.setHint(_ViewHint["default"].INTERACTING, -1);
    this.resolveConstraints(opt_duration, opt_resolutionDirection, opt_anchor);
  };
  /**
   * Get a valid position for the view center according to the current constraints.
   * @param {import("./coordinate.js").Coordinate|undefined} targetCenter Target center position.
   * @param {number} [opt_targetResolution] Target resolution. If not supplied, the current one will be used.
   * This is useful to guess a valid center position at a different zoom level.
   * @return {import("./coordinate.js").Coordinate|undefined} Valid center position.
   */


  View.prototype.getConstrainedCenter = function (targetCenter, opt_targetResolution) {
    var size = this.getViewportSize_(this.getRotation());
    return this.constraints_.center(targetCenter, opt_targetResolution || this.getResolution(), size);
  };
  /**
   * Get a valid zoom level according to the current view constraints.
   * @param {number|undefined} targetZoom Target zoom.
   * @param {number} [opt_direction=0] Indicate which resolution should be used
   * by a renderer if the view resolution does not match any resolution of the tile source.
   * If 0, the nearest resolution will be used. If 1, the nearest lower resolution
   * will be used. If -1, the nearest higher resolution will be used.
   * @return {number|undefined} Valid zoom level.
   */


  View.prototype.getConstrainedZoom = function (targetZoom, opt_direction) {
    var targetRes = this.getResolutionForZoom(targetZoom);
    return this.getZoomForResolution(this.getConstrainedResolution(targetRes, opt_direction));
  };
  /**
   * Get a valid resolution according to the current view constraints.
   * @param {number|undefined} targetResolution Target resolution.
   * @param {number} [opt_direction=0] Indicate which resolution should be used
   * by a renderer if the view resolution does not match any resolution of the tile source.
   * If 0, the nearest resolution will be used. If 1, the nearest lower resolution
   * will be used. If -1, the nearest higher resolution will be used.
   * @return {number|undefined} Valid resolution.
   */


  View.prototype.getConstrainedResolution = function (targetResolution, opt_direction) {
    var direction = opt_direction || 0;
    var size = this.getViewportSize_(this.getRotation());
    return this.constraints_.resolution(targetResolution, direction, size);
  };

  return View;
}(_Object["default"]);
/**
 * @param {Function} callback Callback.
 * @param {*} returnValue Return value.
 */


function animationCallback(callback, returnValue) {
  setTimeout(function () {
    callback(returnValue);
  }, 0);
}
/**
 * @param {ViewOptions} options View options.
 * @return {import("./centerconstraint.js").Type} The constraint.
 */


function createCenterConstraint(options) {
  if (options.extent !== undefined) {
    var smooth = options.smoothExtentConstraint !== undefined ? options.smoothExtentConstraint : true;
    return (0, _centerconstraint.createExtent)(options.extent, options.constrainOnlyCenter, smooth);
  }

  var projection = (0, _proj.createProjection)(options.projection, 'EPSG:3857');

  if (options.multiWorld !== true && projection.isGlobal()) {
    var extent = projection.getExtent().slice();
    extent[0] = -Infinity;
    extent[2] = Infinity;
    return (0, _centerconstraint.createExtent)(extent, false, false);
  }

  return _centerconstraint.none;
}
/**
 * @param {ViewOptions} options View options.
 * @return {{constraint: import("./resolutionconstraint.js").Type, maxResolution: number,
 *     minResolution: number, minZoom: number, zoomFactor: number}} The constraint.
 */


function createResolutionConstraint(options) {
  var resolutionConstraint;
  var maxResolution;
  var minResolution; // TODO: move these to be ol constants
  // see https://github.com/openlayers/openlayers/issues/2076

  var defaultMaxZoom = 28;
  var defaultZoomFactor = 2;
  var minZoom = options.minZoom !== undefined ? options.minZoom : DEFAULT_MIN_ZOOM;
  var maxZoom = options.maxZoom !== undefined ? options.maxZoom : defaultMaxZoom;
  var zoomFactor = options.zoomFactor !== undefined ? options.zoomFactor : defaultZoomFactor;
  var multiWorld = options.multiWorld !== undefined ? options.multiWorld : false;
  var smooth = options.smoothResolutionConstraint !== undefined ? options.smoothResolutionConstraint : true;
  var showFullExtent = options.showFullExtent !== undefined ? options.showFullExtent : false;
  var projection = (0, _proj.createProjection)(options.projection, 'EPSG:3857');
  var projExtent = projection.getExtent();
  var constrainOnlyCenter = options.constrainOnlyCenter;
  var extent = options.extent;

  if (!multiWorld && !extent && projection.isGlobal()) {
    constrainOnlyCenter = false;
    extent = projExtent;
  }

  if (options.resolutions !== undefined) {
    var resolutions = options.resolutions;
    maxResolution = resolutions[minZoom];
    minResolution = resolutions[maxZoom] !== undefined ? resolutions[maxZoom] : resolutions[resolutions.length - 1];

    if (options.constrainResolution) {
      resolutionConstraint = (0, _resolutionconstraint.createSnapToResolutions)(resolutions, smooth, !constrainOnlyCenter && extent, showFullExtent);
    } else {
      resolutionConstraint = (0, _resolutionconstraint.createMinMaxResolution)(maxResolution, minResolution, smooth, !constrainOnlyCenter && extent, showFullExtent);
    }
  } else {
    // calculate the default min and max resolution
    var size = !projExtent ? // use an extent that can fit the whole world if need be
    360 * _proj.METERS_PER_UNIT[_Units["default"].DEGREES] / projection.getMetersPerUnit() : Math.max((0, _extent.getWidth)(projExtent), (0, _extent.getHeight)(projExtent));
    var defaultMaxResolution = size / _common.DEFAULT_TILE_SIZE / Math.pow(defaultZoomFactor, DEFAULT_MIN_ZOOM);
    var defaultMinResolution = defaultMaxResolution / Math.pow(defaultZoomFactor, defaultMaxZoom - DEFAULT_MIN_ZOOM); // user provided maxResolution takes precedence

    maxResolution = options.maxResolution;

    if (maxResolution !== undefined) {
      minZoom = 0;
    } else {
      maxResolution = defaultMaxResolution / Math.pow(zoomFactor, minZoom);
    } // user provided minResolution takes precedence


    minResolution = options.minResolution;

    if (minResolution === undefined) {
      if (options.maxZoom !== undefined) {
        if (options.maxResolution !== undefined) {
          minResolution = maxResolution / Math.pow(zoomFactor, maxZoom);
        } else {
          minResolution = defaultMaxResolution / Math.pow(zoomFactor, maxZoom);
        }
      } else {
        minResolution = defaultMinResolution;
      }
    } // given discrete zoom levels, minResolution may be different than provided


    maxZoom = minZoom + Math.floor(Math.log(maxResolution / minResolution) / Math.log(zoomFactor));
    minResolution = maxResolution / Math.pow(zoomFactor, maxZoom - minZoom);

    if (options.constrainResolution) {
      resolutionConstraint = (0, _resolutionconstraint.createSnapToPower)(zoomFactor, maxResolution, minResolution, smooth, !constrainOnlyCenter && extent, showFullExtent);
    } else {
      resolutionConstraint = (0, _resolutionconstraint.createMinMaxResolution)(maxResolution, minResolution, smooth, !constrainOnlyCenter && extent, showFullExtent);
    }
  }

  return {
    constraint: resolutionConstraint,
    maxResolution: maxResolution,
    minResolution: minResolution,
    minZoom: minZoom,
    zoomFactor: zoomFactor
  };
}
/**
 * @param {ViewOptions} options View options.
 * @return {import("./rotationconstraint.js").Type} Rotation constraint.
 */


function createRotationConstraint(options) {
  var enableRotation = options.enableRotation !== undefined ? options.enableRotation : true;

  if (enableRotation) {
    var constrainRotation = options.constrainRotation;

    if (constrainRotation === undefined || constrainRotation === true) {
      return (0, _rotationconstraint.createSnapToZero)();
    } else if (constrainRotation === false) {
      return _rotationconstraint.none;
    } else if (typeof constrainRotation === 'number') {
      return (0, _rotationconstraint.createSnapToN)(constrainRotation);
    } else {
      return _rotationconstraint.none;
    }
  } else {
    return _rotationconstraint.disable;
  }
}
/**
 * Determine if an animation involves no view change.
 * @param {Animation} animation The animation.
 * @return {boolean} The animation involves no view change.
 */


function isNoopAnimation(animation) {
  if (animation.sourceCenter && animation.targetCenter) {
    if (!(0, _coordinate.equals)(animation.sourceCenter, animation.targetCenter)) {
      return false;
    }
  }

  if (animation.sourceResolution !== animation.targetResolution) {
    return false;
  }

  if (animation.sourceRotation !== animation.targetRotation) {
    return false;
  }

  return true;
}
/**
 * @param {import("./coordinate.js").Coordinate} coordinate Coordinate.
 * @param {import("./size.js").Size} size Box pixel size.
 * @param {import("./pixel.js").Pixel} position Position on the view to center on.
 * @param {number} resolution Resolution.
 * @param {number} rotation Rotation.
 * @return {import("./coordinate.js").Coordinate} Shifted center.
 */


function calculateCenterOn(coordinate, size, position, resolution, rotation) {
  // calculate rotated position
  var cosAngle = Math.cos(-rotation);
  var sinAngle = Math.sin(-rotation);
  var rotX = coordinate[0] * cosAngle - coordinate[1] * sinAngle;
  var rotY = coordinate[1] * cosAngle + coordinate[0] * sinAngle;
  rotX += (size[0] / 2 - position[0]) * resolution;
  rotY += (position[1] - size[1] / 2) * resolution; // go back to original angle

  sinAngle = -sinAngle; // go back to original rotation

  var centerX = rotX * cosAngle - rotY * sinAngle;
  var centerY = rotY * cosAngle + rotX * sinAngle;
  return [centerX, centerY];
}

var _default = View;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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